import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WaitlistModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSuccess(true);
        toast.success("You're on the list!");
      } else {
        toast.error("Something went wrong. Try again.");
      }
    } catch (error) {
      // Store locally if API fails
      const waitlist = JSON.parse(localStorage.getItem("xtrip_waitlist") || "[]");
      waitlist.push({ email, timestamp: new Date().toISOString() });
      localStorage.setItem("xtrip_waitlist", JSON.stringify(waitlist));
      setSuccess(true);
      toast.success("You're on the list!");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/95 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-card border border-border p-8 animate-fade-in-up" style={{ animationFillMode: 'forwards' }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-primary/20 border border-primary flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
              You're In
            </h2>
            <p className="text-muted-foreground mb-6">
              We'll notify you when XTrip launches.
            </p>
            <Button
              onClick={onClose}
              variant="outline"
              className="border-border text-muted-foreground hover:text-foreground"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            {/* Coming Soon Badge */}
            <div className="text-center mb-6">
              <span className="genre-badge text-primary border-primary">
                COMING SOON
              </span>
            </div>

            {/* Headline */}
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground text-center mb-2">
              Be the first to know
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              Join the waitlist for early access to curated music journeys across Europe.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-input border-border h-12 text-center"
                data-testid="waitlist-email-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow h-12 uppercase tracking-wider font-semibold"
                data-testid="waitlist-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Join Waitlist"
                )}
              </Button>
            </form>

            <p className="text-xs text-muted-foreground text-center mt-6">
              No spam. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default WaitlistModal;
